import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertColor } from '@mui/material/Alert';
import { ToastSnackBar } from 'modules/Settings/ToastSnackBar';
import { StartEndSettingsLanding } from 'modules/Settings/StartEndSettingsLanding';
import { SmartSuggestionLanding } from 'modules/Settings/SmartSuggestionLanding';
import { useFeatureFlag } from 'modules/common/hooks';
import AutoPilotDrawer from './AutoPilotDrawer';
import AutoPilotSettings from './AutoPilotSettings';
import { MySettingsTab } from './utils';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: theme.spacing(130),
    minHeight: theme.spacing(65),
    maxWidth: theme.spacing(150),
    borderRadius: theme.spacing(1.2)
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily,
    color: theme.palette.text.secondary
  },
  container: {
    width: '100%',
    height: '100%'
  },
  divContent: {
    display: 'flex',
    overflow: 'hidden'
  }
}));

const SettingsDialog = ({
  open,
  onClose
}: SettingsDialogProps): JSX.Element => {
  const classes = useStyles();
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [showAlert, setAlert] = useState(false);

  const [selected, setSelected] = useState<MySettingsTab>(
    MySettingsTab.AUTOPOPULATE
  );
  const { formatMessage } = useIntl();

  const alertMessage = useMemo(() => {
    if (selected === MySettingsTab.STARTENDTIME)
      return formatMessage({
        id: `settingsDialog.${severity}.startEndSettings`
      });
    else if (selected === MySettingsTab.SMARTSUGGESTION)
      return formatMessage({
        id: `settingsDialog.${severity}.smartSuggestionSettings`
      });
    return formatMessage({
      id: `settingsDialog.${severity}.autoPilotSettings`
    });
  }, [formatMessage, selected, severity]);

  const handleCloseAlert = useCallback(() => {
    setAlert(false);
  }, []);
  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');

  return (
    <>
      <Dialog
        data-testid="settings-dialog"
        open={open}
        classes={{
          paper: classes.dialog,
          container: classes.container
        }}
        onClose={isTiwVpatEnabled ? onClose : undefined}
      >
        <DialogTitle className={classes.title}>
          <FormattedMessage id="settingsDialog.settings" />
        </DialogTitle>
        <div className={classes.divContent}>
          <AutoPilotDrawer selected={selected} setSelected={setSelected} />
          {selected === MySettingsTab.AUTOPOPULATE && (
            <AutoPilotSettings
              setAlert={setAlert}
              setSeverity={setSeverity}
              onClose={onClose}
            />
          )}
          {selected === MySettingsTab.STARTENDTIME && (
            <StartEndSettingsLanding
              onClose={onClose}
              setAlert={setAlert}
              setSeverity={setSeverity}
            />
          )}
          {selected === MySettingsTab.SMARTSUGGESTION && (
            <SmartSuggestionLanding
              onClose={onClose}
              setAlert={setAlert}
              setSeverity={setSeverity}
            />
          )}
        </div>
      </Dialog>
      <ToastSnackBar
        show={showAlert}
        category={severity}
        message={alertMessage}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default SettingsDialog;
